import {
    BuyerResidentialUnitDocument,
    ITaskSelection,
    BuyerProjectData,
    PossibleChatFollower,
    SimplifiedBuyerUser,
    BuyerOptionPresetImage,
    BudgetDto,
    ChangeRequestAttachment,
    ChangeRequestProgressInfo,
    ResidentialUnitDocumentVersion,
    BuyerPermissions,
    TaskFilterData,
    BuyerTask,
    ProjectTerms,
    BuyerDocument,
    BuyerOfferDocumentGroup,
} from '@/types';
import {
    BygrModule,
    getCoreApiUrl,
    LandingPageDataDTO,
    WhiteLabelColorsDto,
} from '@ygtechnologies/bygr-frontend-component-library';
import { getApiUrl, generateApiUrl, request } from '@/utils/ajax';

export function getBuyerPermissions(buyerProfileId: number): Promise<BuyerPermissions> {
    return request(`api/buyer`, { query: { profileId: buyerProfileId } }).then((x) => x.json());
}

export function getBuyerDocuments(residentialUnitId: number, categories?: number[] | null): Promise<BuyerDocument[]> {
    return request(`api/buyer/${residentialUnitId}/documents`, { query: { categories } }).then((x) => x.json());
}

export function searchFullBuyerDocuments(
    residentialUnitId: number,
    categories?: number[] | null
): Promise<BuyerResidentialUnitDocument[]> {
    return request(`api/buyer/${residentialUnitId}/residential-documents`, { query: { categories } }).then((x) => x.json());
}

export function getBuyerOfferDocuments(residentialUnitId: number): Promise<BuyerOfferDocumentGroup[]> {
    return request(`api/buyer/${residentialUnitId}/offer-documents`).then((x) => x.json());
}

export function getBuyerDocumentVersions(
    residentialUnitId: number,
    documentId: string
): Promise<ResidentialUnitDocumentVersion[]> {
    return request(`api/buyer/${residentialUnitId}/documents/${documentId}/versions`).then((x) => x.json());
}

export function getBuyerTasks(
    residentialUnitId: number,
    categories,
    taskStatuses,
    projectDeadlineId,
    tags,
    showExpired: boolean
): Promise<BuyerTask[]> {
    return request(`api/buyer/${residentialUnitId}/tasks`, {
        query: {
            categories,
            taskStatuses: taskStatuses ? taskStatuses.join(',') : null,
            projectDeadlineId,
            projectTagIds: tags ? tags.join(',') : null,
            showExpired,
        },
    }).then((x) => x.json());
}
export function getBuyerTaskFilterData(residentialUnitId: number): Promise<TaskFilterData> {
    return request(`api/buyer/${residentialUnitId}/tasks/filter-data`).then((x) => x.json());
}

export function getBuyerTask(residentialUnitId: number, taskId: number) {
    return request(`api/buyer/${residentialUnitId}/tasks/${taskId}`).then((x) => x.json());
}

export interface OptionSelectionValue {
    buyerOptionGroupId: number;
    selectedLinkGroupItemId: number;
    customFieldData: string;
}

export function getTaskOptions(
    residentialUnitId: number,
    taskId: number,
    wizardSelections: OptionSelectionValue[]
): Promise<ITaskSelection[]> {
    return request(`api/buyer/${residentialUnitId}/tasks/${taskId}/options`, {
        json: true,
        query: {
            wizardSelections: wizardSelections.map((x) => `${x.buyerOptionGroupId}-${x.selectedLinkGroupItemId}`).join(','),
        },
    }).then((x) => x.json());
}

export function selectOptions(residentialUnitId: number, selections: OptionSelectionValue[]) {
    return request(`api/buyer/${residentialUnitId}/tasks/selections`, {
        method: 'PUT',
        json: true,
        body: selections,
    });
}

export function getBuyerBudget(residentialUnitId: number): Promise<BudgetDto> {
    return request(`api/buyer/${residentialUnitId}/budget`).then((x) => x.json());
}

export function getBuyerBudgetExportUrl(residentialUnitId: number) {
    return getApiUrl() + `/api/buyer/${residentialUnitId}/budget/export`;
}

export function sendSelections(residentialUnitId: number, projectDeadlineId: number, oneTimeCode: string) {
    return request(`api/buyer/${residentialUnitId}/send-tasks`, {
        method: 'POST',
        json: true,
        body: { projectDeadlineId, oneTimeCode },
    });
}

export function getOpenChangeRequestCount(residentialUnitId: number): Promise<number> {
    return request(`api/buyer/${residentialUnitId}/change-requests/open-count`).then((x) => x.json());
}

export function getClosedChangeRequestCount(residentialUnitId: number): Promise<number> {
    return request(`api/buyer/${residentialUnitId}/change-requests/accepted-count`).then((x) => x.json());
}

export function getChangeRequestsProgress(residentialUnitId: number): Promise<ChangeRequestProgressInfo[]> {
    return request(`api/buyer/${residentialUnitId}/change-requests`).then((x) => x.json());
}

export function getChangeRequestProgress(
    residentialUnitId: number,
    changeRequestId: number
): Promise<ChangeRequestProgressInfo> {
    return request(`api/buyer/${residentialUnitId}/change-requests/${changeRequestId}`).then((x) => x.json());
}

export interface SubmitChangeRequestOptions {
    subject: string;
    description: string;

    attachments: ChangeRequestAttachment[];
}

export function submitChangeRequest(residentialUnitId: number, changeRequest: SubmitChangeRequestOptions): Promise<number> {
    return request(`api/buyer/${residentialUnitId}/change-requests`, {
        method: 'POST',
        json: true,
        body: changeRequest,
    }).then((x) => x.json());
}

export function acceptBuyerOffer(residentialUnitId: number, buyerOfferId: number, oneTimeCode: string) {
    return request(`api/buyer/${residentialUnitId}/offers/${buyerOfferId}/accept`, {
        method: 'POST',
        json: true,
        body: {
            oneTimeCode,
        },
    });
}

export function rejectBuyerOffer(residentialUnitId: number, buyerOfferId: number, reason) {
    return request(`api/buyer/${residentialUnitId}/offers/${buyerOfferId}/reject`, {
        method: 'POST',
        json: true,
        body: {
            reason,
        },
    });
}

export function getBuyerProjectInfo(residentialUnitId: number): Promise<BuyerProjectData> {
    return request(`api/buyer/${residentialUnitId}/project`).then((x) => x.json());
}

export function getBuyerLandingPage(residentialUnitId: number): Promise<LandingPageDataDTO> {
    return request(
        `v1/buyer/${residentialUnitId}/landing-page`,
        {
            query: { bygrModule: BygrModule.ChangeManagement },
        },
        getCoreApiUrl()
    ).then((x) => x.json());
}

export function checkOneTimeCode(code: string) {
    return request(`api/buyer/check-one-time-code`, {
        method: 'POST',
        json: true,
        body: { code },
    });
}

export function addAttachmentToChangeRequest(
    residentialUnitId: number,
    changeRequestId: number,
    attachments: ChangeRequestAttachment[]
): Promise<ChangeRequestAttachment[]> {
    return request(`api/buyer/${residentialUnitId}/change-requests/${changeRequestId}/attachment`, {
        method: 'POST',
        json: true,
        body: { attachments },
    }).then((x) => x.json());
}

export function getBuyerDocumentTypes() {
    return request(`api/buyer/document-types`).then((x) => x.json());
}

export function getOfferProfileName(residentialUnitId, buyerOfferId) {
    return request(`api/buyer/${residentialUnitId}/offers/${buyerOfferId}/profile-name`).then((x) => x.json());
}

export interface OptionSignatureInfo {
    clientKey: string;
    options: BuyerTask[];
    presets: OptionPreset[];
    totalPrice: number;
}

export interface OfferSignatureInfo {
    clientKey: string;
    buyerOfferId: number;
}

export function prepareOptionSignature(residentialUnitId: number, deadlineId: number): Promise<OptionSignatureInfo> {
    return request(`api/buyer/${residentialUnitId}/option-signature`, {
        method: 'POST',
        json: true,
        body: { projectDeadlineId: deadlineId },
    }).then((x) => x.json());
}

export function signOptionSignature(residentialUnitId: number, clientKey: string, oneTimeCode: string): Promise<any> {
    return request(`api/buyer/${residentialUnitId}/option-signature/${clientKey}/sign`, {
        method: 'POST',
        json: true,
        body: { oneTimeCode },
    });
}

export function prepareOfferSignature(residentialUnitId: number, buyerOfferId: number): Promise<OfferSignatureInfo> {
    return request(`api/buyer/${residentialUnitId}/offer-signature`, {
        method: 'POST',
        json: true,
        body: { buyerOfferId },
    }).then((x) => x.json());
}

export function signOfferSignature(residentialUnitId: number, clientKey: string, oneTimeCode: string): Promise<any> {
    return request(`api/buyer/${residentialUnitId}/offer-signature/${clientKey}/sign`, {
        method: 'POST',
        json: true,
        body: { oneTimeCode },
    });
}

export function getProjectTermsAsBuyer(residentialUnitId: number): Promise<ProjectTerms> {
    return request(`api/buyer/${residentialUnitId}/terms`).then((x) => x.json());
}
export interface OptionPresetGroup {
    id: number;
    name: string;
    description: string;
    canChange: boolean;
    presets: OptionPreset[];
    forceSelection: boolean;
}

export interface OptionPreset {
    id: number;
    name: string;
    description: string;
    images: BuyerOptionPresetImage[];
    selected: boolean;
    price?: number;
}

export interface OptionPresetItem {
    task: BuyerTask;
    presetOption: ITaskSelection;
    presetOptions: ITaskSelection[];
}

export interface OptionPresetItems {
    items: OptionPresetItem[];
    totalOptionsPrice: number;
}

export function getOptionPresets(residentialUnitId: number): Promise<OptionPresetGroup[]> {
    return request(`api/buyer/${residentialUnitId}/option-presets`).then((x) => x.json());
}

export function getOptionPresetItems(
    residentialUnitId: number,
    optionPresetId: number,
    changesOnly: boolean
): Promise<OptionPresetItems> {
    return request(`api/buyer/${residentialUnitId}/option-presets/${optionPresetId}/items`, {
        query: { changesOnly },
    }).then((x) => x.json());
}

// export function getCanChangePreset(residentialUnitId: number): Promise<boolean> {
//     return request(`api/buyer/${residentialUnitId}/option-presets/can-change`).then((x) => x.json());
// }

export function applyOptionPreset(
    residentialUnitId: number,
    optionPresetId: number,
    extraSelections: OptionSelectionValue[]
): Promise<any> {
    return request(`api/buyer/${residentialUnitId}/option-presets/${optionPresetId}/apply`, {
        method: 'POST',
        json: true,
        body: extraSelections,
    });
}

export function getPossibleChatFollowersAsBuyer(residentialUnitId: number): Promise<PossibleChatFollower[]> {
    return request(`api/buyer/${residentialUnitId}/chat-followers`).then((x) => x.json());
}

export function addCommentThreadAsBuyer(
    residentialUnitId: number,
    name: string,
    followerProjectMemberIds: number[]
): Promise<number> {
    return request(`api/buyer/${residentialUnitId}/chat-thread`, {
        method: 'POST',
        json: true,
        body: {
            name,
            followerProjectMemberIds,
        },
    }).then((x) => x.json());
}
export function getResidentialUnitBuyerUsers(residentialUnitId: number): Promise<SimplifiedBuyerUser[]> {
    return request(`api/buyer/${residentialUnitId}/buyer-users`).then((x) => x.json());
}
export interface BuyerWhiteLabelInfo {
    isEnabled: boolean;
    shadowEnabled?: boolean;
    /// Raw JSON of the rubus-ui v2 Theme. When non-null, consumers should use this
    /// directly and ignore `colors`. When null, fall back to the legacy mapper.
    themeJson?: string;
    colors: WhiteLabelColorsDto;
    editable: boolean;
}
export function getBuyerThemeInfo(residentialUnitId: number, module: BygrModule): Promise<BuyerWhiteLabelInfo> {
    return request(`${getCoreApiUrl()}/api/buyer/${residentialUnitId}/theme/new`, {
        method: 'GET',
        json: true,
        query: {
            bygrModule: module,
        },
    }).then((x) => x.json());
}

export function getBuyerWhiteLabelCssUrl(residentialUnitId: number) {
    return generateApiUrl(`/api/buyer/${residentialUnitId}/css`);
}
